import React, { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import MainLayout from './components/Layout/MainLayout';
import LoadingSpinner from './components/UI/LoadingSpinner';
import { BIRTHDAY_EXTENSION_EMAILS } from './constants/Accesos';
import DedicatoriaCreate from './components/Dedicatoria/DedicatoriaCreate';

// Import components with proper dynamic imports
const Login = lazy(() => import('./components/Login').then(module => ({ default: module.Login })));
const Register = lazy(() => import('./components/Register').then(module => ({ default: module.Register })));
const Home = lazy(() => import('./components/Home').then(module => ({ default: module.Home })));
const Detalle = lazy(() => import('./components/Detalle').then(module => ({ default: module.Detalle })));
const Dedicatoria = lazy(() => import('./components/Dedicatoria/Dedicatoria').then(module => ({ default: module.default || module.Dedicatoria })));
const DedicatoriaDetalle = lazy(() => import('./components/Dedicatoria/DedicatoriaDetalle').then(module => ({ default: module.default || module.DedicatoriaDetalle })));
const PedidosGeneral = lazy(() => import('./components/PedidosGeneral/PedidosGeneral').then(module => ({ default: module.default || module.PedidosGeneral })));
const PedidosProgramados = lazy(() => import('./components/PedidosProgramados/PedidosProgramados').then(module => ({ default: module.default || module.PedidosProgramados })));
const Reemprimir = lazy(() => import('./components/Reemprimir/Reemprimir').then(module => ({ default: module.default || module.Reemprimir })));
const ReemprimirTiendas = lazy(() => import('./components/Reemprimir/ReemprimirTiendas').then(module => ({ default: module.default || module.ReemprimirTiendas })));
const PedidosTienda = lazy(() => import('./components/Page/PedidosTienda/PedidosTienda').then(module => ({ default: module.default || module.PedidosTienda })));
const PedidosProgramadosGeneral = lazy(() => import('./components/Page/PedidosProgramadosGeneral/PedidosProgramadosGeneral').then(module => ({ default: module.default || module.PedidosProgramadosGeneral })));
const DedicatoriaTienda = lazy(() => import('./components/Page/DedicatoriaTiendas/DedicatoriaTienda').then(module => ({ default: module.default || module.DedicatoriaTienda })));
const BirthdayValidation = lazy(() => import('./components/birthday/BirthdayValidation').then(module => ({ default: module.default || module.BirthdayValidation })));
const BirthdayExtension = lazy(() => import('./components/birthday/BirthdayExtension').then(module => ({ default: module.default || module.BirthdayExtension })));
const Reclamos = lazy(() => import('./components/Reclamos/Reclamos').then(module => ({ default: module.default || module.Reclamos })));
const CesePersonal = lazy(() => import('./components/CesePersonal/CesePersonal').then(module => ({ default: module.default || module.CesePersonal })));
const CesePersonalCreate = lazy(() => import('./components/CesePersonal/CesePersonalCreate').then(module => ({ default: module.default || module.CesePersonalCreate })));
const CesePersonalApproval = lazy(() => import('./components/CesePersonal/CesePersonalApproval').then(module => ({ default: module.default || module.CesePersonalApproval })));
const CesePersonalApprovalDetail = lazy(() => import('./components/CesePersonal/CesePersonalApprovalDetail').then(module => ({ default: module.default || module.CesePersonalApprovalDetail })));
const CesePersonalHistory = lazy(() => import('./components/CesePersonal/CesePersonalHistory').then(module => ({ default: module.default || module.CesePersonalHistory })));
const CesePersonalObservedDocuments = lazy(() => import('./components/CesePersonal/CesePersonalObservedDocuments').then(module => ({ default: module.default || module.CesePersonalObservedDocuments })));
const CesePersonalLiberacion = lazy(() => import('./components/CesePersonal/CesePersonalLiberacion').then(module => ({ default: module.default || module.CesePersonalLiberacion })));
const Incidencias = lazy(() => import('./components/Incidencias/Incidencias').then(module => ({ default: module.default || module.Incidencias })));

// Componente de carga para Suspense con manejo de errores
const Loader = ({ children }) => {
  const ErrorBoundary = React.lazy(() => 
    import('./components/ErrorBoundary').then(module => ({
      default: module.default || module.ErrorBoundary
    }))
  );

  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    }>
      <ErrorBoundary>
        {children}
      </ErrorBoundary>
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <MainLayout>
        <Routes>
          <Route path="/login" element={
            <Loader>
              <Login />
            </Loader>
          } />
          
          <Route path="/register" element={
            <Loader>
              <Register />
            </Loader>
          } />
          
          <Route path="/" element={
            <ProtectedRoute>
              <Loader>
                <Home />
              </Loader>
            </ProtectedRoute>
          } />
          
          <Route path="/reemprimir" element={
            <ProtectedRoute>
              <Loader>
                <Reemprimir />
              </Loader>
            </ProtectedRoute>
          } />
          
          <Route path="/reemprimir-tiendas" element={
            <ProtectedRoute>
              <Loader>
                <ReemprimirTiendas />
              </Loader>
            </ProtectedRoute>
          } />
          
          <Route path="/detalle/:id" element={
            <ProtectedRoute>
              <Loader>
                <Detalle />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/dedicatoria" element={
            <ProtectedRoute>
              <Loader>
                <Dedicatoria />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/dedicatoria/:id" element={
            <ProtectedRoute>
              <Loader>
                <DedicatoriaDetalle />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/pedidosgeneral" element={
            <ProtectedRoute>
              <Loader>
                <PedidosGeneral />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/pedidosprogramados" element={
            <ProtectedRoute>
              <Loader>
                <PedidosProgramados />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/pedidoecommerce" element={
            <ProtectedRoute>
              <Loader>
                <PedidosTienda />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/pedidosprogramadosgeneral" element={
            <ProtectedRoute>
              <Loader>
                <PedidosProgramadosGeneral />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/dedicatoriatienda" element={
            <ProtectedRoute>
              <Loader>
                <DedicatoriaTienda />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/dedicatoriacreate" element={
            <ProtectedRoute>
              <Loader>
                <DedicatoriaCreate />
              </Loader>
            </ProtectedRoute>
          } />
          
          <Route path="/validar-cumpleanos" element={
            <ProtectedRoute>
              <Loader>
                <BirthdayValidation />
              </Loader>
            </ProtectedRoute>
          } />
          
          <Route path="/validar-cumpleanos-todos" element={
            <ProtectedRoute>
              <Loader>
                <BirthdayValidation />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/validar-cumpleanos/extensiones" element={
            <ProtectedRoute allowedEmails={BIRTHDAY_EXTENSION_EMAILS}>
              <Loader>
                <BirthdayExtension />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/reclamos" element={
            <ProtectedRoute>
              <Loader>
                <Reclamos />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/cese-personal" element={
            <ProtectedRoute>
              <Loader>
                <CesePersonal />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/cese-personal/create" element={
            <ProtectedRoute>
              <Loader>
                <CesePersonalCreate />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/cese-personal/approval" element={
            <ProtectedRoute>
              <Loader>
                <CesePersonalApproval />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/cese-personal/approval/:id" element={
            <ProtectedRoute>
              <Loader>
                <CesePersonalApprovalDetail />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/cese-personal/history" element={
            <ProtectedRoute>
              <Loader>
                <CesePersonalHistory />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/cese-personal/liberacion" element={
            <ProtectedRoute>
              <Loader>
                <CesePersonalLiberacion />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/cese-personal/observados/:id" element={
            <ProtectedRoute>
              <Loader>
                <CesePersonalObservedDocuments />
              </Loader>
            </ProtectedRoute>
          } />

          <Route path="/incidencias" element={
            <ProtectedRoute>
              <Loader>
                <Incidencias />
              </Loader>
            </ProtectedRoute>
          } />
        </Routes>
      </MainLayout>
    </AuthProvider>
  );
}

export default App;
